import * as React from "react";
import { Bell, User, Grip } from "lucide-react";
import { useBuildVersion } from "@/hooks/use-build-version";
import iconAerotrak from "@/assets/icon/icon-aerotrak.svg";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import AppTile from "@/components/apps/app-tile";
import { IconoirRender } from "@/components/icons/iconoir-map";
import { getAeroTrakAppDtos } from "@/hooks/services/aerotrak/handlers/aerotrak-apps-api";
import { cn } from "@/lib/utils";
import { toastManager } from "@/lib/toast-manager";
import {
  NotificationPanel,
  Notification,
  getCategoryRelatedObjectNames,
  mapNotificationDto,
  libraryRelatedObjectNames,
} from "@/components/notifications";
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "@/hooks/services/workchestra/handler/notifications-api";
import { useNotificationStore } from "@/stores/notification-store";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { GetAppConfigHandler } from "@/hooks/services/workchestra/handler/config-api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { logout } from "@/hooks/services/workchestra/handler/auth";
import { UserMenu } from "@/components/layout/user-menu";

export function Footer() {
  const buildVersion = useBuildVersion();
  const environment = import.meta.env.VITE_ENVIRONMENT || "Development";
  const showEnvironment = environment.toLowerCase() !== "production";

  // Mobile menu state
  const [isAppsOpen, setIsAppsOpen] = React.useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);

  // Apps state
  const [apps, setApps] = React.useState<
    Array<{
      id: number;
      name: string;
      description?: string;
      iconLibrary?: string;
      iconName?: string;
      iconColor?: string | null;
      url: string;
      status?: string;
    }>
  >([]);
  const [appsLoading, setAppsLoading] = React.useState(false);
  const [appsError, setAppsError] = React.useState<string | null>(null);

  // Notification state
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const { unreadCount, decrementUnreadCount, resetUnreadCount, initUnreadCount } = useNotificationStore();

  // Fetch total unread count on mount (deduplicated via store)
  React.useEffect(() => {
    initUnreadCount();
  }, [initUnreadCount]);

  // Fetch app config for redirect URL
  const { data: appConfig } = useGetData(GetAppConfigHandler());

  const aerotrakRedirectUrl = appConfig?.appUrls?.aerotrak || "/";

  const sortNotificationsByNewest = React.useCallback(
    (items: Notification[]) =>
      [...items].sort(
        (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
      ),
    [],
  );

  // Fetch notifications based on category
  const fetchNotificationsByCategory = React.useCallback(
    async (category: string) => {
      try {
        if (category.toLowerCase() === "all") {
          const [allRes, libraryRes] = await Promise.all([
            fetchNotifications({
              page: 1,
              itemsPerPage: 10,
              relatedObjectName: undefined,
            }),
            fetchNotifications({
              page: 1,
              itemsPerPage: 10,
              relatedObjectName: libraryRelatedObjectNames,
            }),
          ]);

          const merged = [...allRes.items];
          const existingIds = new Set(merged.map((item) => item.id));

          libraryRes.items.forEach((item) => {
            if (!existingIds.has(item.id)) {
              merged.push(item);
            }
          });

          setNotifications(
            sortNotificationsByNewest(merged.map(mapNotificationDto)),
          );
          return;
        }

        const relatedObjectNames = getCategoryRelatedObjectNames(category);
        const notifRes = await fetchNotifications({
          page: 1,
          itemsPerPage: 10,
          relatedObjectName: relatedObjectNames,
        });
        setNotifications(
          sortNotificationsByNewest(notifRes.items.map(mapNotificationDto)),
        );
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load notifications";
        toastManager.showError(errorMessage);
      }
    },
    [libraryRelatedObjectNames, sortNotificationsByNewest],
  );

  // Handle category change
  const handleCategoryChange = React.useCallback(
    (category: string) => {
      fetchNotificationsByCategory(category);
    },
    [fetchNotificationsByCategory],
  );

  // Fetch apps when apps popover opens
  React.useEffect(() => {
    if (!isAppsOpen) return;
    let cancelled = false;
    setAppsLoading(true);
    getAeroTrakAppDtos()
      .then((res) => {
        if (cancelled) return;
        setApps(res.filter((a) => a.isAssigned !== false));
        setAppsError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed to load apps";
        setAppsError(msg);
      })
      .finally(() => {
        if (!cancelled) setAppsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAppsOpen]);

  // Fetch notifications when notifications popover opens
  React.useEffect(() => {
    if (!isNotificationsOpen) return;

    let cancelled = false;

    const fetchInitialData = async () => {
      try {
        const [appsRes] = await Promise.all([
          getAeroTrakAppDtos(),
          fetchNotificationsByCategory("all"),
        ]);

        if (!cancelled) {
          setApps(appsRes.filter((a) => a.isAssigned !== false));
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to load notification data";
        toastManager.showError(errorMessage);
      }
    };

    fetchInitialData();

    return () => {
      cancelled = true;
    };
  }, [fetchNotificationsByCategory, isNotificationsOpen]);

  const handleLogout = async () => {
    logout();
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
      );
      decrementUnreadCount();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to mark notification as read";
      toastManager.showError(errorMessage);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      resetUnreadCount();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to mark all notifications as read";
      toastManager.showError(errorMessage);
    }
  };

  const handleViewAll = () => {
    setIsNotificationsOpen(false);
    const rawWorkchestraUrl = import.meta.env.VITE_WORKCHESTRA_BASE_URL || "";
    const workchestraBaseUrl = rawWorkchestraUrl.endsWith("/")
      ? rawWorkchestraUrl.slice(0, -1)
      : rawWorkchestraUrl;
    window.location.href = `${workchestraBaseUrl}/workchestra/notifications`;
  };

  return (
    <footer className="aero:relative aero:border-neutral-300 aero:border-top">
      {/* Desktop footer with version and environment */}
      <div className="aero:hidden aero:md:flex aero:bg-brand-primary-700 aero:text-white aero:items-center aero:justify-center aero:px-10 aero:py-2.5 aero:border-none">
        <div className="aero:w-full aero:flex aero:items-center aero:justify-between">
          {/* Left side: Logo and Title */}
          <div className="aero:flex aero:items-center aero:gap-1">
            <a
              href={aerotrakRedirectUrl}
              rel="noopener noreferrer"
              className="aero:flex aero:items-center aero:gap-1"
            >
              <div className="aero:w-[18px] aero:h-[18px] aero:flex-shrink-0 aero:mb-0.5">
                <img
                  src={iconAerotrak}
                  alt="Logo"
                  className="aero:w-full aero:h-full aero:object-cover"
                />
              </div>
              <p className="aero:text-sm aero:font-bold aero:leading-5 aero:text-white aero:whitespace-nowrap">
                Integrated MRO workspace by ST Engineering
              </p>
            </a>
          </div>

          {/* Right side: Version, Divider, Environment */}
          <div className="aero:flex aero:items-center aero:gap-3">
            <p className="aero:text-sm aero:font-normal aero:leading-5 aero:text-white aero:whitespace-nowrap">
              Version Number : {buildVersion}
            </p>

            {showEnvironment && (
              <>
                {/* Vertical Divider */}
                <div className="aero:h-[19px] aero:w-0 aero:flex aero:items-center aero:justify-center">
                  |
                </div>

                <div className="aero:flex aero:items-center aero:gap-1">
                  <p className="aero:text-sm aero:font-normal aero:leading-normal aero:text-white">
                    Environment :
                  </p>
                  <div className="aero:bg-white aero:border aero:border-neutral-900 aero:rounded-full aero:px-2 aero:py-1">
                    <div className="aero:flex aero:items-center aero:justify-center">
                      <p className="aero:text-xs aero:font-medium aero:leading-4 aero:text-center aero:text-foreground aero:whitespace-nowrap">
                        {environment}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile copyright bar pinned below the menu */}
      <div className="aero:md:hidden aero:fixed aero:bottom-0 aero:left-0 aero:right-0 aero:border-none">
        {/* Menu */}
        <div className="aero:border-t aero:h-14 aero:bg-neutral-50 aero:px-5 aero:shadow-[0_-5px_4px_0_rgba(190,190,190,0.25)] aero:border-none">
          <nav className="aero:grid aero:h-full aero:grid-cols-3 aero:place-items-center">
            {/* Apps Popover */}
            <Popover open={isAppsOpen} onOpenChange={setIsAppsOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Apps"
                  aria-haspopup="menu"
                  aria-expanded={isAppsOpen}
                  className="aero:flex aero:h-full aero:w-full aero:items-center aero:justify-center aero:text-neutral-700 aero:hover:text-foreground"
                >
                  <Grip className="aero:h-6 aero:w-6" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                sideOffset={8}
                className="aero:w-[calc(100vw-40px)] aero:max-w-[400px] aero:p-4 aero:rounded-xl aero:border aero:border-neutral-300 aero:bg-white aero:shadow-lg"
              >
                {appsLoading && (
                  <div className="aero:text-xs aero:text-neutral-500">
                    Loading...
                  </div>
                )}
                {!appsLoading && appsError && (
                  <div className="aero:text-xs aero:text-red-600">
                    {appsError}
                  </div>
                )}
                {!appsLoading && !appsError && apps.length === 0 && (
                  <div className="aero:text-xs aero:text-neutral-500">
                    No apps assigned.
                  </div>
                )}
                {!appsLoading && !appsError && apps.length > 0 && (
                  <div
                    role="list"
                    className={cn("aero:grid aero:grid-cols-3 aero:gap-3")}
                  >
                    {apps.map((app) => (
                      <div key={app.id} role="listitem">
                        <AppTile
                          id={app.id}
                          name={app.name}
                          href={app.url}
                          icon={
                            app.iconLibrary === "iconoir" && app.iconName ? (
                              <IconoirRender
                                name={app.iconName}
                                color={app.iconColor || undefined}
                                size={28}
                                strokeWidth={1.6}
                              />
                            ) : (
                              <span className="aero:text-[10px] aero:text-neutral-400">
                                NA
                              </span>
                            )
                          }
                          newTab={false}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Notifications Popover */}
            <Popover
              open={isNotificationsOpen}
              onOpenChange={setIsNotificationsOpen}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Notifications"
                  aria-haspopup="menu"
                  aria-expanded={isNotificationsOpen}
                  className="aero:flex aero:h-full aero:w-full aero:items-center aero:justify-center aero:text-neutral-700 aero:hover:text-foreground"
                >
                  <span className="aero:relative aero:inline-flex">
                    <Bell className="aero:h-6 aero:w-6" />
                    {unreadCount > 0 && (
                      <span className="aero:absolute aero:-right-0.5 aero:-top-1 aero:flex aero:h-3 aero:min-w-3 aero:items-center aero:justify-center aero:!rounded-full aero:bg-red-600 aero:text-[9px] aero:leading-none aero:font-bold aero:text-white">
                        {unreadCount}
                      </span>
                    )}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="center"
                sideOffset={8}
                className="aero:w-[calc(100vw-40px)] aero:max-w-[400px] aero:p-0 aero:border-0 aero:bg-transparent aero:shadow-none"
              >
                <NotificationPanel
                  notifications={notifications}
                  apps={apps}
                  onMarkAsRead={handleMarkAsRead}
                  onMarkAllAsRead={handleMarkAllAsRead}
                  onViewAll={handleViewAll}
                  onCategoryChange={handleCategoryChange}
                />
              </PopoverContent>
            </Popover>

            {/* User Account Popover */}
            <Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Account"
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                  className="aero:flex aero:h-full aero:w-full aero:items-center aero:justify-center aero:text-neutral-700 aero:hover:text-foreground"
                >
                  <Avatar className="aero:size-6 aero:transition-colors aero:ring-1 aero:ring-transparent aero:hover:ring-neutral-300">
                    <AvatarFallback>
                      <User className="aero:h-5 aero:w-5" />
                    </AvatarFallback>
                  </Avatar>
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="end"
                sideOffset={8}
                className="aero:w-[200px] aero:p-0 aero:overflow-hidden aero:rounded-lg aero:border aero:border-neutral-300 aero:shadow-md"
              >
                <UserMenu
                  onLogout={handleLogout}
                  onItemSelect={() => setIsUserMenuOpen(false)}
                />
              </PopoverContent>
            </Popover>
          </nav>
        </div>
        {/* Footer under the menu */}
        <div className="aero:bg-[#2F4F4F] aero:text-white aero:h-10 aero:flex aero:items-center aero:justify-center aero:px-5">
          <a
            href={aerotrakRedirectUrl}
            rel="noopener noreferrer"
            className="aero:flex aero:items-center aero:gap-1"
          >
            <div className="aero:w-[18px] aero:h-[18px] aero:flex-shrink-0 aero:mb-0.5">
              <img
                src={iconAerotrak}
                alt="Logo"
                className="aero:w-full aero:h-full aero:object-cover"
              />
            </div>
            <p className="aero:text-xs aero:font-bold aero:leading-4 aero:text-white aero:whitespace-nowrap">
              Integrated MRO workspace by ST Engineering
            </p>
          </a>
        </div>
      </div>
    </footer>
  );
}
